import { useState } from "react";
import { Link } from "react-router-dom";
import { ThemeToggle } from "../sirius/ThemeProvider";
import { LanguageToggle, useT } from "../i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** /forgot-password — first leg of the self-service password reset flow.
 *  User enters their email; backend always returns 200 (anti-enumeration),
 *  and the success screen is the SAME whether the email exists or not. */
export default function ForgotPassword() {
  const { lang } = useT();
  const isEn = lang === "en";
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/auth/password-reset/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(typeof d.detail === "string" ? d.detail : (isEn ? "Could not send reset email" : "Kunne ikke sende e-post"));
      }
      // Always reach success state — the API never reveals whether the
      // address was registered (prevents email-enumeration).
      setSent(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="srs" style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }} data-testid="forgot-password-root">
      <nav style={{ padding: "16px 28px", borderBottom: "1px solid var(--srs-line)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Link to="/" style={{ color: "var(--srs-ink)", textDecoration: "none", fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 22 }}>EuroPilot</Link>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}><LanguageToggle /><ThemeToggle /></div>
      </nav>

      <main style={{ flex: 1, display: "grid", placeItems: "center", padding: 32 }}>
        {sent ? (
          <div className="srs-card" style={{ width: "100%", maxWidth: 460, padding: 40, borderRadius: 24 }} data-testid="forgot-password-sent">
            <div className="srs-mono" style={{ fontSize: 10, letterSpacing: "0.3em", color: "var(--srs-good)", marginBottom: 14 }}>✓ {isEn ? "EMAIL SENT" : "E-POST SENDT"}</div>
            <h1 className="srs-display" style={{ fontSize: 28, fontWeight: 500, lineHeight: 1.15, margin: "0 0 14px 0" }}>
              {isEn ? "Check your inbox" : "Sjekk innboksen din"}
            </h1>
            <p style={{ fontSize: 14, color: "var(--srs-ink-dim)", lineHeight: 1.6, margin: "0 0 20px 0" }}>
              {isEn
                ? <>If <strong style={{color:"var(--srs-ink)"}}>{email}</strong> is registered with EuroPilot, we&apos;ve sent a password-reset link. The link is valid for 1 hour.</>
                : <>Hvis <strong style={{color:"var(--srs-ink)"}}>{email}</strong> er registrert hos EuroPilot, har vi sendt en lenke for å sette nytt passord. Lenken er gyldig i 1 time.</>
              }
            </p>
            <p style={{ fontSize: 12, color: "var(--srs-ink-faint)", lineHeight: 1.55, margin: "0 0 26px 0" }}>
              {isEn ? "Didn't get it? Check your spam folder, or wait 60 seconds and try again." : "Fikk du ingen e-post? Sjekk søppelpost-mappen, eller vent 60 sekunder og prøv igjen."}
            </p>
            <Link to="/login" className="srs-cta-ghost" style={{ display: "inline-block", textAlign: "center" }} data-testid="forgot-password-back-to-login">
              {isEn ? "← Back to login" : "← Tilbake til innlogging"}
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="srs-card" style={{ width: "100%", maxWidth: 420, padding: 40, borderRadius: 24 }} data-testid="forgot-password-form">
            <div className="srs-mono" style={{ fontSize: 10, letterSpacing: "0.3em", color: "var(--srs-sun)", marginBottom: 14 }}>// {isEn ? "RESET PASSWORD" : "TILBAKESTILL PASSORD"}</div>
            <h1 className="srs-display" style={{ fontSize: 30, fontWeight: 500, letterSpacing: "-0.02em", margin: "0 0 14px 0", lineHeight: 1.1 }}>
              {isEn ? "Forgot password?" : "Glemt passord?"}
            </h1>
            <p style={{ fontSize: 14, color: "var(--srs-ink-dim)", lineHeight: 1.6, margin: "0 0 24px 0" }}>
              {isEn
                ? "Enter the email you signed up with. We'll send you a one-time link to set a new password."
                : "Skriv inn e-posten du registrerte deg med. Vi sender deg en engangs-lenke for å sette et nytt passord."}
            </p>
            <label style={labelCss}>{isEn ? "EMAIL" : "E-POST"}</label>
            <input
              type="email"
              required
              autoFocus
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ola@nordmann.no"
              style={inputCss}
              data-testid="forgot-password-email-input"
            />
            {error && <div style={errorCss} data-testid="forgot-password-error">{error}</div>}
            <button type="submit" disabled={submitting || !email} className="srs-cta"
              style={{ marginTop: 22, width: "100%", justifyContent: "center" }} data-testid="forgot-password-submit">
              {submitting ? (isEn ? "Sending…" : "Sender…") : (isEn ? "Send reset link →" : "Send lenke →")}
            </button>
            <div style={{ marginTop: 20, textAlign: "center", fontSize: 12, color: "var(--srs-ink-faint)" }}>
              <Link to="/login" style={{ color: "var(--srs-ink-dim)", textDecoration: "none" }} data-testid="forgot-password-back-link">
                ← {isEn ? "Back to login" : "Tilbake til innlogging"}
              </Link>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}

const labelCss = { display: "block", fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: "0.22em", fontWeight: 600, color: "var(--srs-ink-dim)", textTransform: "uppercase", marginBottom: 8 };
const inputCss = { width: "100%", background: "var(--srs-surface-2)", border: "1px solid var(--srs-line)", borderRadius: 10, padding: "12px 14px", fontSize: 14, color: "var(--srs-ink)", fontFamily: "inherit", outline: "none", boxSizing: "border-box" };
const errorCss = { marginTop: 16, padding: "10px 12px", background: "color-mix(in srgb, var(--srs-warn) 12%, transparent)", border: "1px solid var(--srs-warn)", borderRadius: 10, color: "var(--srs-warn)", fontSize: 12, lineHeight: 1.5 };
